/* ==========================================================================
   CADOO — global theme JavaScript.
   Replaces the prototype DC/React runtime. Uses the real Shopify Ajax Cart API.

   NOTE: the 10% welcome offer's modal/form still own their own behaviour
   inside sections/cadoo-welcome-offer.liquid. This file only knows how to
   open and close panels generically, how to keep the floating launcher out
   of the way while another overlay is open, and — see "WELCOME OFFER —
   shared claimed-state" below — the single shared source of truth for
   whether this visitor has already claimed that offer, since several
   independent triggers across the theme (the desktop launcher, the mobile
   homepage hero gift tag, the mobile-nav fallback, the footer fallback)
   all need to agree on that instantly, without duplicating the check.
   ========================================================================== */
(function () {
  'use strict';

  var CFG = window.CadooConfig || {};
  var routes = CFG.routes || {};
  var $ = function (s, r) { return (r || document).querySelector(s); };
  var $$ = function (s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); };

  var ESC_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return ESC_MAP[c]; });
  }

  /* ---------------- scroll restoration on back/forward --------------------
     A shopper who opens a product from deep in a collection (or from a
     filtered/sorted view) and taps the browser's Back control should land
     back where they were, not at the top of the page. The browser's own
     scroll restoration is not reliable for that here, because going back
     to this theme's pages can reload the document over the network rather
     than serve it from the back/forward cache - so this saves the scroll
     offset into the outgoing history entry on 'pagehide' (fires exactly
     once, right as the shopper leaves) and restores it on 'pageshow',
     which fires for a normal back/forward navigation whether or not the
     browser happened to cache the page.

     'pageshow' also fires on the very first load of a fresh entry (a menu
     link, a direct URL), but a fresh entry has no saved cdScrollTop on it
     yet, so the restore below is a no-op and the page opens at the top as
     normal - only an entry this tab has already left carries a saved
     position. Nothing here touches history.pushState/popstate: every
     navigation in this theme (filters, sorting, pagination, product
     links) is a normal full-page load, never client-side routing. */
  function cdSaveScrollPosition() {
    try {
      var state = window.history.state;
      if (!state || typeof state !== 'object') state = {};
      state.cdScrollTop = window.scrollY;
      window.history.replaceState(state, '');
    } catch (e) {}
  }
  window.addEventListener('pagehide', cdSaveScrollPosition);

  window.addEventListener('pageshow', function () {
    var state = window.history.state;
    var saved = state && state.cdScrollTop;
    if (typeof saved !== 'number' || !isFinite(saved) || saved < 0) return;

    /* Deferred one frame so the restored/reloaded page has completed its
       first layout pass before we measure how far it can actually scroll. */
    requestAnimationFrame(function () {
      var el = document.scrollingElement || document.documentElement;
      var maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
      window.scrollTo({ top: Math.min(saved, maxTop), left: 0, behavior: 'instant' });
    });
  });

  /* ---------------- money -------------------------------------------------
     Shopify is the source of truth for currency and price formatting. This
     renders the store's own money_format string (exposed on CadooConfig by
     layout/theme.liquid). No currency symbol is hardcoded and no conversion
     of any kind happens here. */
  function formatAmount(cents, precision, thousands, decimal) {
    if (isNaN(cents) || cents == null) return '0';
    var num = (Number(cents) / 100).toFixed(precision);
    var parts = num.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  }

  function money(cents) {
    var fmt = CFG.moneyFormat || '{{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = fmt.match(placeholder);
    var value;
    switch (match && match[1]) {
      case 'amount_no_decimals': value = formatAmount(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = formatAmount(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatAmount(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = formatAmount(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = formatAmount(cents, 0, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = formatAmount(cents, 2, ' ', '.'); break;
      default: value = formatAmount(cents, 2, ',', '.');
    }
    return match ? fmt.replace(placeholder, value) : value;
  }
  window.CadooMoney = money;

  /* ---------------- transient error toast ----------------
     Quick Add has no inline error slot of its own - it is a single control on
     a card, sometimes inside a drawer. It used to surface a failure through a
     browser alert(), which is unannounced to assistive technology, blocks the
     page, and reads like a browser fault rather than a shop message. This is a
     small polite-but-assertive live region instead: it is created once, on
     first use, so it costs nothing until something actually goes wrong. */
  var toastEl = null;
  var toastTimer = null;

  function toast(message) {
    if (!message) return;
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'cd-toast';
      toastEl.setAttribute('role', 'alert');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    toastEl.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      if (toastEl) toastEl.classList.remove('is-on');
    }, 6000);
  }

  /* ---------------- overlay / focus management ---------------- */
  var openStack = [];
  var lastFocus = null;

  function parkLauncher(on) {
    var l = $('[data-cd-offer-launch]');
    if (!l || l.hidden) return;
    l.style.visibility = on ? 'hidden' : '';
  }

  function trap(e) {
    if (!openStack.length || e.key !== 'Tab') return;
    var panel = openStack[openStack.length - 1];
    var f = $$('a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])', panel)
      .filter(function (el) { return el.offsetParent !== null; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function openPanel(el) {
    if (!el) return;
    lastFocus = document.activeElement;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    var ov = $('#cd-overlay');
    if (ov) ov.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    openStack.push(el);
    parkLauncher(true);
    var focusable = $('[data-cd-autofocus]', el) || $('button,a[href],input', el);
    if (focusable) setTimeout(function () { focusable.focus(); }, 60);
  }

  function closePanel(el) {
    if (!el) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    /* Retiring the drawer hides the transient "added" banner it may be
       showing, and stops any pending auto-hide timer, so reopening the
       cart later never surfaces a stale confirmation. */
    if (el.id === 'cd-cart') hideAddedBanner();
    openStack = openStack.filter(function (x) { return x !== el; });
    /* One announcement for every way a panel can close - the close control,
       the scrim, Escape, or a programmatic close. Anything that needs to tidy
       up after itself listens for this instead of intercepting four separate
       routes and inevitably missing one. */
    try {
      document.dispatchEvent(new CustomEvent('cadoo:panel-closed', { detail: { panel: el } }));
    } catch (e) {}
    if (!openStack.length) {
      var ov = $('#cd-overlay');
      if (ov) ov.classList.remove('is-open');
      document.body.style.overflow = '';
      parkLauncher(false);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
  }
  function closeAll() { openStack.slice().forEach(closePanel); }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openStack.length) {
      var top = openStack[openStack.length - 1];
      closePanel(top);
    }
    trap(e);
  });
  document.addEventListener('click', function (e) {
    if (e.target && e.target.id === 'cd-overlay') closeAll();
  });

  /* ---------------- announcement bar ---------------- */
  function initAnnounce() {
    var bar = $('[data-cd-announce]');
    if (!bar) return;
    var msgs = $$('.cd-announce__msg', bar);
    if (msgs.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var i = 0, paused = false;
    var interval = parseInt(bar.getAttribute('data-interval') || '6500', 10);
    setInterval(function () {
      if (paused) return;
      msgs[i].classList.remove('is-on');
      i = (i + 1) % msgs.length;
      msgs[i].classList.add('is-on');
    }, interval);
    bar.addEventListener('mouseenter', function () { paused = true; });
    bar.addEventListener('mouseleave', function () { paused = false; });
    bar.addEventListener('focusin', function () { paused = true; });
    bar.addEventListener('focusout', function () { paused = false; });
  }

  /* ---------------- mega menus ---------------- */
  function initMega() {
    var header = $('[data-cd-header]');
    if (!header) return;
    var closeTimer = null;
    function closeMenus() {
      $$('.cd-mega', header).forEach(function (m) { m.classList.remove('is-open'); });
      $$('[data-menu]', header).forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    }
    $$('[data-menu]', header).forEach(function (btn) {
      var panel = $('.cd-mega[data-menu-panel="' + btn.getAttribute('data-menu') + '"]', header);
      if (!panel) return;
      function open() {
        clearTimeout(closeTimer);
        closeMenus();
        panel.classList.add('is-open');
        btn.setAttribute('aria-expanded', 'true');
      }
      btn.addEventListener('mouseenter', open);
      btn.addEventListener('focus', open);
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (panel.classList.contains('is-open')) closeMenus(); else open();
      });
      [btn, panel].forEach(function (el) {
        el.addEventListener('mouseleave', function () { closeTimer = setTimeout(closeMenus, 160); });
        el.addEventListener('mouseenter', function () { clearTimeout(closeTimer); });
      });
    });
    header.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeMenus(); });
    document.addEventListener('click', function (e) { if (!header.contains(e.target)) closeMenus(); });
  }

  /* ---------------- accordions ---------------- */
  function initAccordions(root) {
    $$('[data-cd-acc-btn]', root || document).forEach(function (btn) {
      if (btn.__cd) return; btn.__cd = 1;
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (panel) panel.classList.toggle('is-open', !open);
      });
    });
    $$('[data-cd-footer-col]').forEach(function (h) {
      if (h.__cd) return; h.__cd = 1;
      h.addEventListener('click', function () {
        if (window.innerWidth > 767) return;
        h.parentNode.classList.toggle('is-open');
      });
    });
  }

  /* ---------------- generic open/close triggers ---------------- */
  document.addEventListener('click', function (e) {
    var o = e.target.closest && e.target.closest('[data-cd-open]');
    if (o) {
      e.preventDefault();
      var t = document.getElementById(o.getAttribute('data-cd-open'));
      if (t) { if (t.id === 'cd-cart') renderCart(); openPanel(t); }
      return;
    }
    var c = e.target.closest && e.target.closest('[data-cd-close]');
    if (c) {
      e.preventDefault();
      closePanel(c.closest('.cd-drawer, .cd-search, .cd-offer, .cd-amodal'));
    }
  });

  /* ---------------- WISHLIST ----------------
     Device-local list (localStorage, browser-scoped) for a signed-out
     visitor. For a signed-in customer, assets/cadoo-secret-santa.js installs
     a PROVIDER through CadooWishlist.use(); from then on the displayed list,
     the per-heart state and the toggle come from server-authoritative
     account state held in memory by that module, and localStorage is never
     read for heart state. There is exactly one painter (syncWish) either
     way, so a provider swap never produces two competing repaints.

     Provider contract (every member optional except get):
       get()               -> array of handles the Saved views should show
       toggle(handle)      -> handles CadooWishlist.toggle(handle) compat calls
       state(button, list) -> true/false for this specific heart, or null to
                              fall back to "handle is in list"
       label(button, on, title) -> accessible name override, or null
     While signed in and no provider has taken control yet, the engine shows
     an empty list rather than another customer's device list. */
  var WKEY = 'cadoo_wishlist_v1';
  var wishProvider = null;
  function wishLocalGet() { try { return JSON.parse(localStorage.getItem(WKEY) || '[]'); } catch (e) { return []; } }
  function wishLocalSet(list) { try { localStorage.setItem(WKEY, JSON.stringify(list)); } catch (e) {} }
  function wishGet() {
    if (wishProvider) { try { var p = wishProvider.get(); return Array.isArray(p) ? p.slice() : []; } catch (e) { return []; } }
    if (CFG.signedIn === true) return [];
    return wishLocalGet();
  }
  function wishSet(list) { wishLocalSet(list); syncWish(); }
  function wishToggle(handle) {
    if (wishProvider && typeof wishProvider.toggle === 'function') return wishProvider.toggle(handle);
    if (CFG.signedIn === true) return false;
    var l = wishLocalGet(), i = l.indexOf(handle);
    if (i > -1) l.splice(i, 1); else l.push(handle);
    wishSet(l);
    return l.indexOf(handle) > -1;
  }
  function wishUse(provider) {
    wishProvider = provider && typeof provider.get === 'function' ? provider : null;
    syncWish();
  }
  function syncWish() {
    var l = wishGet();
    $$('[data-cd-wish]').forEach(function (b) {
      var on = null;
      if (wishProvider && typeof wishProvider.state === 'function') { try { on = wishProvider.state(b, l); } catch (e) { on = null; } }
      if (on === null || on === undefined) on = l.indexOf(b.getAttribute('data-cd-wish')) > -1;
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      var t = b.getAttribute('data-title');
      if (t) {
        var label = null;
        if (wishProvider && typeof wishProvider.label === 'function') { try { label = wishProvider.label(b, on, t); } catch (e) { label = null; } }
        /* Stable accessible name: the state is announced through aria-pressed,
           so the name no longer flips between "Save" and "Remove". */
        b.setAttribute('aria-label', label || ('Save ' + t));
      }
    });
    $$('[data-cd-wish-count]').forEach(function (el) {
      el.textContent = l.length ? l.length : '';
      el.style.display = l.length ? '' : 'none';
    });
    var hb = $('[data-cd-wish-trigger]');
    if (hb) hb.setAttribute('aria-label', l.length ? 'Saved gifts, ' + l.length : 'Saved gifts, empty');
    document.dispatchEvent(new CustomEvent('cadoo:wishlist', { detail: { list: l } }));
  }
  window.CadooWishlist = {
    get: wishGet,
    toggle: wishToggle,
    sync: syncWish,
    use: wishUse,
    local: { get: wishLocalGet, set: wishLocalSet }
  };

  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('[data-cd-wish]');
    if (!b) return;
    e.preventDefault();
    wishToggle(b.getAttribute('data-cd-wish'));
  });

  /* ---------------- WELCOME OFFER — shared claimed-state (localStorage, browser-scoped) ----------------
     Single source of truth for whether this visitor has already claimed
     the 10% welcome offer, shared by every trigger that can open it: the
     desktop floating launcher and the mobile homepage hero gift tag (both
     in sections/cadoo-hero-v2.liquid and sections/cadoo-welcome-offer.liquid),
     the mobile-nav fallback and the footer fallback (snippets/cadoo-drawers.liquid,
     sections/cadoo-footer.liquid). Every one of those elements just needs
     the data-cd-offer-trigger attribute - offerApplyState() below finds all
     of them and shows/hides them together, so no file has to duplicate the
     localStorage read or its own hide/show logic.

     sections/cadoo-welcome-offer.liquid's own modal script is the ONLY
     caller of claim() - it calls window.Cadoo.claim() the instant Shopify
     confirms a successful signup (never on open/close/dismiss/error), and
     nothing else may write this key. */
  var OFFER_KEY = 'cadoo_welcome_offer';
  function offerClaimed() { try { return localStorage.getItem(OFFER_KEY) === 'done'; } catch (e) { return false; } }
  function offerApplyState() {
    var done = offerClaimed();
    $$('[data-cd-offer-trigger]').forEach(function (el) { el.hidden = done; });
  }
  function offerClaim() {
    if (offerClaimed()) return; // idempotent - never re-notify
    try { localStorage.setItem(OFFER_KEY, 'done'); } catch (e) {}
    offerApplyState();
  }

  /* ==========================================================================
     CART (real Shopify Ajax API)

     ONE RULE: every line the customer chose is a real line. A gift add-on
     selected in the post-add modal is an ordinary, independently purchasable
     cart item - it is drawn in the drawer and on the cart page, it counts
     towards the badge, it contributes to the subtotal and to free-delivery
     progress, and its quantity belongs to the customer alone. Nothing in this
     file hides a line, locks a quantity to another line, or removes a line on
     the customer's behalf - with one exception: a paid wrapping charge whose
     gift has been removed is swept with it (see "paid gift wrapping follows
     its gift" below).

     The previous product-level gifting system (a premium presentation line
     tied to its parent by _cadoo_gift_group / _cadoo_gift_addon, kept in
     quantity lockstep by reconcileCart(), hidden from every total, and
     silently deleted when its parent went) has been removed outright. Add-ons
     are now visible, independent purchases, and there is no dormant code left
     that could ever quietly change one.

     Order-level gifting is unchanged: presentation is uniform, and the single
     message card is an order attribute collected on the Delivery Setup step
     (sections/cadoo-delivery-setup.liquid), never a per-line property.

     Checkout: the CHECKOUT control in the main "Your gift bag" drawer is a
     static, server-rendered form declared once in
     snippets/cadoo-drawers.liquid. This code only ever updates the total
     text and toggles [hidden] on it — it never rebuilds or replaces that
     form, so a re-render, a slow network response, or a future bug in this
     file can never leave Checkout without a working handler.
     ========================================================================== */
  var cartState = null;

  /* Marks a line the customer accepted from the post-add modal. Informational
     only: it changes how the line is LABELLED and nothing else - not its
     quantity, not its price, not whether it is counted. */
  function isModalAddon(item) {
    var p = item && item.properties;
    return !!(p && p._cadoo_addon_source === 'post_add_modal');
  }

  /* ---------------- cart quantity: limits, sequencing, error handling ------
     Why this block exists: /cart/change.js answers a rejected update (for
     example asking for more units than are in stock) with an HTTP 422 error
     object - {status, message, description} - not a cart. That object used to
     be stored as cartState and painted, and because it has no items[] the
     drawer said "Your bag is empty" and the header count cleared, although
     the bag on the server was untouched.

     The rules now:
       - Only a real cart object (one with an items[] array) is ever stored,
         counted or drawn. An error, a network failure or an unreadable
         response keeps the last confirmed cart on screen.
       - One quantity update at a time. Taps that arrive while one is in
         flight are ignored, every response is sequenced so an older one can
         never overwrite a newer one, and the controls always unlock.
       - After a rejected update the authoritative cart (and its limits) is
         re-read and redrawn immediately, with an inline, screen-reader
         announced message on the line - no close/reopen needed.
       - Prevention: sections/cadoo-cart-limits.liquid reports, per variant in
         the cart, the Shopify rules that cap its quantity - tracked inventory
         with "deny" overselling, and quantity rules (min/max/increment).
         Untracked variants and variants that continue selling get no cap.
         Quantities of the same variant on separate lines are added together.
         The server stays the final authority because stock can change at
         any moment; the client cap only avoids requests we know will fail. */
  var LIMITS_SECTION = 'cadoo-cart-limits';
  var cartLimits = Object.create(null);  /* variant id -> {inv, rmax, min, step} */
  var lineNotes = Object.create(null);   /* line key -> transient {text, kind} */
  var cartSeq = 0;        /* issue order of every cart read/write */
  var cartApplied = 0;    /* sequence of the cart currently in cartState */
  var limitsSeq = 0;
  var limitsApplied = 0;
  var cartBusy = null;    /* the in-flight quantity update, if any */
  var CHANGE_TIMEOUT = 15000;

  function isCart(c) {
    return !!(c && typeof c === 'object' && Object.prototype.toString.call(c.items) === '[object Array]');
  }

  function applyCart(c, seq) {
    if (!isCart(c)) return false;
    if (seq < cartApplied) return false; /* an older response arriving late */
    cartApplied = seq;
    cartState = c;
    updateCartCount(c);
    return true;
  }

  function readLimits(html, seq) {
    if (!html || seq < limitsApplied) return false;
    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var el = doc.querySelector('[data-cd-cart-limits]');
      var list = el ? JSON.parse(el.textContent) : null;
      if (Object.prototype.toString.call(list) !== '[object Array]') return false;
      var next = Object.create(null);
      list.forEach(function (l) { if (l && l.v) next[String(l.v)] = l; });
      cartLimits = next;
      limitsApplied = seq;
      return true;
    } catch (e) { return false; }
  }

  function fetchLimits() {
    var seq = ++limitsSeq;
    var base = routes.root || '/';
    return fetch(base + (base.indexOf('?') > -1 ? '&' : '?') + 'sections=' + LIMITS_SECTION, {
      headers: { 'Accept': 'application/json' }, cache: 'no-store', credentials: 'same-origin'
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) { return !!(d && readLimits(d[LIMITS_SECTION], seq)); })
      .catch(function () { return false; });
  }

  /* Reads /cart.js. Resolves with the cart, or null if it could not be read -
     never with an error object. A read waits for an in-flight quantity
     update, so it cannot return the pre-update cart after that update. */
  function getCart(opts) {
    var wait = (cartBusy && !(opts && opts.noWait)) ? cartBusy.then(noop, noop) : Promise.resolve();
    return wait.then(function () {
      var seq = ++cartSeq;
      return fetch(routes.cart + '.js', { headers: { 'Accept': 'application/json' }, cache: 'no-store', credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (c) {
          if (!isCart(c)) return null;
          applyCart(c, seq);
          return cartState;
        })
        .catch(function () { return null; });
    });
  }
  function noop() {}

  /* The badge answers "how many things have I chosen", so it counts every
     real line: main gifts and the add-ons the customer accepted alike. An
     add-on is a separate purchase the customer opted into, and leaving it out
     would understate what the bag actually holds and what it will cost.
     Only ever called with a real cart (see isCart). */
  function updateCartCount(c) {
    if (!isCart(c)) return;
    var n = 0;
    c.items.forEach(function (it) { n += it.quantity; });
    $$('[data-cd-cart-count]').forEach(function (el) {
      el.textContent = n ? n : '';
      el.style.display = n ? '' : 'none';
    });
    var b = $('[data-cd-cart-trigger]');
    if (b) b.setAttribute('aria-label', n ? 'Gift bag, ' + n : 'Gift bag, empty');
  }

  function addToCart(payload) {
    return fetch(routes.cart_add + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      return r.json().then(function (d) {
        if (!r.ok) throw new Error(d.description || d.message || 'Could not add to bag.');
        return d;
      });
    });
  }

  function lineByKey(c, key) {
    if (!isCart(c)) return null;
    for (var i = 0; i < c.items.length; i++) if (c.items[i].key === key) return c.items[i];
    return null;
  }

  /* What Shopify allows for this line's variant, given every line of that
     variant already in the bag. cap is null when nothing limits it. */
  function lineLimit(it, c) {
    var l = cartLimits[String(it.variant_id)];
    var total = 0;
    if (isCart(c)) c.items.forEach(function (x) { if (x.variant_id === it.variant_id) total += x.quantity; });
    var out = { cap: null, kind: null, total: total, step: 1, min: 1 };
    if (!l) return out;
    if (typeof l.inv === 'number') { out.cap = Math.max(0, l.inv); out.kind = 'stock'; }
    if (typeof l.rmax === 'number' && l.rmax > 0 && (out.cap === null || l.rmax < out.cap)) { out.cap = l.rmax; out.kind = 'rule'; }
    if (typeof l.step === 'number' && l.step > 1) out.step = l.step;
    if (typeof l.min === 'number' && l.min > 1) out.min = l.min;
    return out;
  }

  /* Short explanation shown beside the quantity when the cap is reached. */
  function limitNote(lim) {
    if (lim.cap === null) return '';
    if (lim.kind === 'rule') {
      if (lim.total > lim.cap) return 'Limit of ' + lim.cap + ' per order. Please lower the quantity.';
      if (lim.total + lim.step > lim.cap) return 'Limit of ' + lim.cap + ' per order.';
      return '';
    }
    if (lim.cap === 0) return 'This gift is now sold out. Please remove it to continue.';
    if (lim.total > lim.cap) return 'Only ' + lim.cap + ' available. Please lower the quantity.';
    if (lim.total === lim.cap) {
      return lim.cap === 1
        ? 'Only 1 available—you have it in your bag.'
        : 'Only ' + lim.cap + ' available—you have all ' + lim.cap + ' in your bag.';
    }
    if (lim.total + lim.step > lim.cap) return 'Only ' + lim.cap + ' available.';
    return '';
  }

  var liveEl = null;
  function announce(text) {
    var panel = $('#cd-cart');
    if (!panel || !text) return;
    if (!liveEl || !panel.contains(liveEl)) {
      liveEl = document.createElement('p');
      liveEl.className = 'cd-sr';
      liveEl.setAttribute('role', 'status');
      liveEl.setAttribute('aria-live', 'polite');
      liveEl.setAttribute('data-cd-cart-live', '');
      panel.appendChild(liveEl);
    }
    liveEl.textContent = '';
    setTimeout(function () { liveEl.textContent = text; }, 60);
  }

  function setLineBusy(key, on) {
    var body = $('[data-cd-cart-body]');
    if (body) {
      if (on) body.setAttribute('aria-busy', 'true'); else body.removeAttribute('aria-busy');
    }
    $$('.cd-line', body || document).forEach(function (el) {
      if (el.getAttribute('data-key') === key) el.classList.toggle('is-updating', !!on);
    });
  }

  function postChangeRaw(payload) {
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, CHANGE_TIMEOUT) : null;
    return fetch(routes.cart_change + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
      signal: ctrl ? ctrl.signal : undefined
    }).then(function (r) {
      return r.json().catch(function () { return null; })
        .then(function (d) { return { ok: r.ok, status: r.status, data: d }; });
    }, function () {
      return { ok: false, status: 0, data: null };
    }).then(function (res) { clearTimeout(timer); return res; });
  }

  /* ---------------- paid gift wrapping follows its gift ----------------
     A paid wrapping line (Individual Gift Wrapping) is tied to the gift it
     wraps by the shared private property _cadoo_wrap_group_id. Removing or
     reducing that gift here used to leave the wrapping charge behind. After
     every confirmed change, if any wrapping line has lost its gift (or now
     covers more units than the gift line has), the same sweep the checkout
     steps use - window.CadooWrap.cleanup() in assets/cadoo-wrapping.js - is
     run inside the same serialized update, so no other drawer change can
     start until it has finished. It only removes or caps wrapping, never adds
     a charge, and matches by group id, so wrapping on any other gift
     (including another line of the same product) is untouched. If it fails,
     the drawer re-reads the real cart, keeps the leftover wrapping visible
     with a note on that line, and the customer can remove it there; the
     checkout steps sweep again before checkout. */
  var WRAP_HANDLE = 'individual-gift-wrapping';
  var WRAP_GROUP = '_cadoo_wrap_group_id';
  var WRAP_CLEANUP_TIMEOUT = 15000;
  var WRAP_LEFT_NOTE = 'The gift this wrapping was for has been removed, but we couldn’t remove its wrapping charge. Please tap Remove on this line.';

  function strayWrapKeys(c) {
    if (!isCart(c)) return [];
    var out = [];
    c.items.forEach(function (w) {
      if (w.handle !== WRAP_HANDLE) return;
      var g = w.properties && w.properties[WRAP_GROUP];
      var gift = null;
      if (g) c.items.forEach(function (x) {
        if (!gift && x.handle !== WRAP_HANDLE && x.properties && x.properties[WRAP_GROUP] === g) gift = x;
      });
      if (!gift || w.quantity > gift.quantity) out.push(w.key);
    });
    return out;
  }

  /* Resolves true when the cart was cleaned up, false when it could not be. */
  function sweepStrayWrapping() {
    if (!strayWrapKeys(cartState).length) return Promise.resolve(true);
    if (!window.CadooWrap || typeof window.CadooWrap.cleanup !== 'function') return Promise.resolve(false);
    var cfg = { wrapHandle: WRAP_HANDLE, premiumHandle: 'premium-gift-presentation', wrapVariantId: 0, eligible: {} };
    var timeout = new Promise(function (resolve) { setTimeout(function () { resolve(null); }, WRAP_CLEANUP_TIMEOUT); });
    return Promise.race([window.CadooWrap.cleanup(cfg).then(function (c) { return c; }, function () { return null; }), timeout])
      .then(function (c) {
        if (isCart(c)) { applyCart(c, ++cartSeq); return !strayWrapKeys(cartState).length; }
        return false;
      });
  }

  function afterWrapSweep(ok) {
    if (ok) return null;
    /* Show the real cart, whatever the sweep managed to do. */
    return getCart({ noWait: true }).then(function () {
      var left = strayWrapKeys(cartState);
      left.forEach(function (k) { lineNotes[k] = { kind: 'error', text: WRAP_LEFT_NOTE }; });
      if (left.length) announce(WRAP_LEFT_NOTE);
      return null;
    });
  }

  /* ---------------- wrapping follows its gift's quantity ----------------
     The drawer uses the same engine as the Presentation and Delivery steps
     (window.CadooWrap in assets/cadoo-wrapping.js), so the one wrapping rule
     holds on every surface:
       - a fully wrapped line (intent "all") follows the quantity Shopify
         ACCEPTS, up and down, in the same serialized update - the wrapping
         line and the total are redrawn together, never charged later;
       - a partly wrapped line (intent "units") keeps its chosen units, capped
         when the quantity drops below them; added units are not wrapped;
       - an unwrapped line never gains wrapping.
     Changing the wrapping line itself sets an absolute number of units
     (CadooWrap.settleWrap), capped to its gift; reaching every unit makes
     the intent "all", fewer makes it "units". */
  function wrapCfg() {
    return { wrapHandle: WRAP_HANDLE, premiumHandle: 'premium-gift-presentation', wrapVariantId: 0, eligible: {} };
  }
  function wrapGroupOf(it) { return (it && it.properties && it.properties[WRAP_GROUP]) || null; }
  /* The gift a wrapping line belongs to, or the wrapping line of a gift. */
  function wrapPartner(c, it) {
    var g = wrapGroupOf(it);
    if (!g || !isCart(c)) return null;
    var wantWrap = it.handle !== WRAP_HANDLE;
    for (var i = 0; i < c.items.length; i++) {
      var x = c.items[i];
      if (x === it || wrapGroupOf(x) !== g) continue;
      if ((x.handle === WRAP_HANDLE) === wantWrap) return x;
    }
    return null;
  }
  function wrapScope(w, gift) {
    if (window.CadooWrap && typeof window.CadooWrap.scopeOf === 'function') return window.CadooWrap.scopeOf(w, gift);
    var s = w.properties && w.properties._cadoo_wrap_scope;
    if (s === 'all' || s === 'units') return s;
    return gift && w.quantity >= gift.quantity ? 'all' : 'units';
  }
  /* Wording for the screen-reader announcement after a change (not shown
     on screen: the wrapping line already shows its quantity and charge). */
  function wrapStatusText(w, gift) {
    if (!w || !gift || !w.quantity || !gift.quantity) return '';
    if (wrapScope(w, gift) === 'all' && w.quantity >= gift.quantity) {
      return 'All units wrapped · ' + money(w.original_price) + ' per item';
    }
    return w.quantity + ' of ' + gift.quantity + (gift.quantity === 1 ? ' unit' : ' units') + ' wrapped';
  }

  var WRAP_FOLLOW_FAIL = 'Quantity updated, but we couldn’t update its gift wrapping to match. Please check the wrapping line below.';
  var BAG_CHANGED = 'Your bag changed in another window, so nothing was changed. We’ve refreshed it—please try again.';

  /* One quantity change through the shared engine. Resolves (never rejects)
     with {ok, status, data, cart, engine, wrapFailed}; status -1 means the
     line was gone or had changed since the drawer was drawn. Returns null
     when the engine cannot handle this line, so the plain path is used. */
  function engineChange(before, qty) {
    var W = window.CadooWrap;
    if (!W || typeof W.editLine !== 'function' || typeof W.settleWrap !== 'function') return null;
    var cfg = wrapCfg();
    var p;
    if (before.handle === WRAP_HANDLE) {
      var g = wrapGroupOf(before);
      if (!g) return null;
      var gift = wrapPartner(cartState, before);
      var scope = gift && qty >= gift.quantity ? 'all' : 'units';
      p = W.settleWrap(g, qty, scope, cfg).then(function (c) {
        return { ok: isCart(c), status: 200, data: null, cart: c, engine: true };
      });
    } else {
      p = W.editLine({ key: before.key, expect: before.quantity }, qty, cfg).then(function (out) {
        return {
          ok: isCart(out.cart), status: 200, data: null, cart: out.cart, engine: true,
          wrapFailed: !!(out.wrap && !out.wrap.ok) || !!(out.services && !out.services.ok)
        };
      });
    }
    return p.then(null, function (err) {
      var k = err && err.kind;
      var status = k === 'rejected' ? 422 : k === 'network' ? 0 : (k === 'gone' || k === 'changed') ? -1 : 500;
      return {
        ok: false, status: status, engine: true, cart: err && err.cart,
        data: k === 'rejected' && err.message ? { description: err.message } : null
      };
    });
  }

  /* Editing or removing any line - a main gift or an add-on - touches only
     that line (and, for a wrapped gift, its own wrapping - see above).
     Removing a main gift leaves its add-on in the bag, still fully editable,
     because the customer chose it separately.

     Resolves with the confirmed cart (or the last confirmed one if the update
     failed). Never rejects, never resolves with an error object. */
  function changeLine(key, qty) {
    if (cartBusy) return cartBusy;
    var before = lineByKey(cartState, key);
    var title = before ? before.product_title : 'This gift';
    var isWrap = !!(before && before.handle === WRAP_HANDLE);
    lineNotes = Object.create(null);   /* notes describe the latest update only */
    setLineBusy(key, true);

    var op = (before && engineChange(before, qty)) ||
      postChangeRaw({ id: key, quantity: qty, sections: LIMITS_SECTION }).then(function (res) {
        res.cart = res.data;
        return res;
      });

    cartBusy = op
      .then(function (res) {
        var seq = ++cartSeq;
        var lseq = ++limitsSeq;
        if (res.ok && isCart(res.cart)) {
          applyCart(res.cart, seq);
          var limits = res.engine ? fetchLimits()
            : Promise.resolve(res.cart.sections ? readLimits(res.cart.sections[LIMITS_SECTION], lseq) : false);
          return limits.then(function () {
            if (isWrap) {
              var gift = before && wrapPartner(cartState, before);
              var w = gift && wrapPartner(cartState, gift);
              announce(w ? 'Gift wrapping updated: ' + wrapStatusText(w, gift) + '.' : 'Gift wrapping removed from your bag.');
              return sweepStrayWrapping().then(afterWrapSweep);
            }
            var after = lineByKey(cartState, key);
            if (qty === 0 || !after) {
              announce(title + ' removed from your bag.');
            } else if (after.quantity < qty) {
              /* Accepted but reduced by Shopify - only stock or rules do that. */
              var lim = lineLimit(after, cartState);
              if (!limitNote(lim)) lineNotes[key] = { kind: 'error', text: 'Only ' + after.quantity + ' could be kept in your bag.' };
              announce(limitNote(lim) || lineNotes[key].text);
            } else {
              var wl = wrapPartner(cartState, after);
              announce('Quantity of ' + title + ' updated to ' + after.quantity + '.' +
                (wl ? ' Gift wrapping: ' + wrapStatusText(wl, after) + '.' : ''));
            }
            if (res.wrapFailed && after) {
              lineNotes[key] = { kind: 'error', text: WRAP_FOLLOW_FAIL };
              announce(WRAP_FOLLOW_FAIL);
            }
            return sweepStrayWrapping().then(afterWrapSweep);
          });
        }
        /* Rejected or failed: reconcile with the authoritative cart. noWait,
           because these reads belong to the update that is still in flight. */
        return Promise.all([getCart({ noWait: true }), fetchLimits()]).then(function () {
          var cur = lineByKey(cartState, key);
          var text;
          if (res.status === -1) {
            text = BAG_CHANGED;
            if (cur) lineNotes[key] = { kind: 'error', text: text };
          } else if (res.status === 422) {
            var lim = cur ? lineLimit(cur, cartState) : null;
            /* Only call it availability when the request itself went past
               the known cap; otherwise show Shopify's own explanation, which
               names the real reason (a quantity rule, for example). */
            var asked = lim ? lim.total - cur.quantity + qty : 0;
            var note = (lim && lim.cap !== null && asked > lim.cap) ? limitNote(lim) : '';
            text = note || (res.data && (res.data.description || res.data.message)) ||
              'We couldn’t change that quantity. Please try again.';
            if (!note && cur) lineNotes[key] = { kind: 'error', text: text };
          } else if (res.status === 429) {
            text = 'Too many updates at once. Please wait a moment and try again.';
            if (cur) lineNotes[key] = { kind: 'error', text: text };
          } else {
            text = res.status === 0
              ? 'We couldn’t reach the shop. Please check your connection and try again.'
              : 'We couldn’t update your bag just now. Please try again.';
            if (cur) lineNotes[key] = { kind: 'error', text: text };
          }
          announce(text);
          return null;
        });
      })
      .catch(noop)
      .then(function () {
        cartBusy = null;
        setLineBusy(key, false);
        drawCart(cartState);
        return cartState;
      });
    return cartBusy;
  }

  function sized(url, w) {
    if (!url) return '';
    return url.indexOf('?') > -1 ? url + '&width=' + w : url + '?width=' + w;
  }

  /* Renders one cart line: image + title/price/quantity row. Every order is
     presented the same way by default now, so there is no per-line gift
     strip or Edit action here — the one order-level message card lives on
     the Delivery Setup step instead.

     A line the customer accepted from the post-add modal carries a quiet
     "Gift add-on" marker so the bag reads clearly, driven by the line's own
     _cadoo_addon_source property. No private property name or value is ever
     printed, and no metafield is fetched to draw the cart.

     Price shown is it.original_line_price (variant price × quantity, before
     any discount). A genuine product sale is still reflected correctly,
     because original_price is simply the variant's current selling price.

     Discounts: this drawer offers NO discount-code entry - codes are entered
     only in Shopify's hosted checkout. A discount that is already in the
     Shopify cart (an automatic discount, or a code that reached the cart some
     other way, e.g. a /discount/CODE link) is DISPLAYED, never applied here:
     a line-level discount is stated beside its line (lineDiscountHtml) and
     every discount is listed once in the footer (updateCartFoot), so the
     estimated total equals cart.total_price. */
  function renderLine(it, idx, c) {
    var lim = lineLimit(it, c);
    var note = lineNotes[it.key];
    var capNote = limitNote(lim);
    var isWrap = it.handle === WRAP_HANDLE;
    var partner = wrapPartner(c, it);
    /* Wrapping can never cover more units than its gift has. */
    var plusOff = (lim.cap !== null && lim.total + lim.step > lim.cap) ||
      (isWrap && !!wrapGroupOf(it) && (!partner || it.quantity >= partner.quantity));
    var minusOff = lim.min > 1 && it.quantity - lim.step < lim.min && it.quantity > 0;
    var noteText = note ? note.text : capNote;
    var noteId = 'cd-qn-' + idx;
    var name = esc(it.product_title);
    var controls =
      '<div class="cd-line__controls"><span class="cd-qty">' +
      '<button type="button" data-cd-qty="-1" aria-label="Decrease quantity of ' + name + '"' +
        (minusOff ? ' aria-disabled="true"' : '') + '>−</button>' +
      '<span>' + it.quantity + '</span>' +
      '<button type="button" data-cd-qty="1" aria-label="Increase quantity of ' + name + '"' +
        (plusOff ? ' aria-disabled="true"' : '') + (noteText ? ' aria-describedby="' + noteId + '"' : '') + '>+</button></span>' +
      '<button type="button" class="cd-remove" data-cd-remove>Remove</button></div>' +
      (noteText
        ? '<p class="cd-line__qtynote' + (note && note.kind === 'error' ? ' is-error' : '') + '" id="' + noteId + '">' + esc(noteText) + '</p>'
        : '');

    return '<div class="cd-line" data-key="' + esc(it.key) + '">' +
      '<div class="cd-line__row">' +
      (it.image ? '<img class="cd-line__img" src="' + sized(it.image, 220) + '" alt="" loading="lazy">' : '<span class="cd-line__img"></span>') +
      '<div class="cd-line__body">' +
      (isModalAddon(it) ? '<p class="cd-line__tag">Gift add-on</p>' : '') +
      '<h4 class="cd-line__title"><a href="' + it.url + '">' + esc(it.product_title) + '</a></h4>' +
      (it.variant_title && it.variant_title !== 'Default Title' ? '<p class="cd-note" style="margin:0 0 4px">' + esc(it.variant_title) + '</p>' : '') +
      '<span class="cd-line__price">' + money(it.original_line_price) + '</span>' +
      lineDiscountHtml(it) +
      pzLineHtml(it) +
      lineWrapHtml(it, partner, isWrap) +
      controls +
      '</div></div>' +
      '</div>';
  }

  /* ---------------- personalized gifts: per-line summary + Edit ----------
     A line added through the personalization form carries the private marker
     _cadoo_personalized = "1" and customer-facing properties (Name/Initials,
     Font, Print colour, Method, Your photo, Instructions). They are drawn
     here from the cart's own data: every value escaped, the photo shown as a
     small thumbnail + "View photo" link (never the raw storage URL), private
     underscore properties never printed. "Edit personalization" opens the
     product page in edit context for this exact line key
     (assets/cadoo-personalize.js), which saves with ONE /cart/change.js
     request against that key - no delete-and-re-add. */
  var PZ_ORDER = ['Name', 'Initials', 'Method', 'Font', 'Print colour', 'Instructions'];
  function pzHosted(v) { return typeof v === 'string' && /^https:\/\/cdn\.shopify\.com\/.+\/uploads\//i.test(v); }
  function pzLineHtml(it) {
    var p = it && it.properties;
    if (!p || p._cadoo_personalized !== '1') return '';
    var rows = [];
    PZ_ORDER.forEach(function (k) {
      if (p[k]) rows.push('<span class="cd-line__pzrow"><span class="cd-line__pzk">' + esc(k) + ':</span> ' + esc(p[k]) + '</span>');
    });
    var photo = p['Your photo'];
    if (pzHosted(photo)) {
      rows.push('<span class="cd-line__pzrow cd-line__pzphoto"><span class="cd-line__pzk">Your photo:</span> ' +
        '<a href="' + esc(photo) + '" target="_blank" rel="noopener" class="cd-line__pzlink">' +
        '<img class="cd-line__pzthumb" src="' + esc(sized(photo, 120)) + '" alt="" loading="lazy" width="36" height="36"> View photo</a></span>');
    }
    if (!rows.length) return '';
    var editUrl = (it.url || '') + ((it.url || '').indexOf('?') > -1 ? '&' : '?') + 'cd_edit=' + encodeURIComponent(it.key);
    return '<div class="cd-line__pz">' + rows.join('') +
      '<a class="cd-line__pzedit" href="' + esc(editUrl) + '">Edit personalization</a></div>';
  }

  /* Wrapping information: the wrapping line says which gift it is for; its
     own quantity and price show what is wrapped and what it costs. */
  (function () {
    if (document.getElementById('cd-cart-extra-css')) return;
    var st = document.createElement('style');
    st.id = 'cd-cart-extra-css';
    st.textContent =
      '.cd-line__wrap,.cd-line__disc{margin:2px 0 0;font-size:12.5px;line-height:1.4;color:var(--cd-muted,#5b6475)}' +
      '.cd-line__disc{color:var(--cd-navy,#14213d);font-weight:600}' +
      '.cd-cart-discounts{margin:0 0 6px;font-size:14px}.cd-cart-discounts[hidden]{display:none}' +
      '.cd-cart-discounts__row{display:flex;justify-content:space-between;gap:12px;padding:2px 0}' +
      '.cd-cart-discounts__row--disc{color:var(--cd-navy,#14213d);font-weight:600}' +
      '.cd-line__pz{margin:4px 0 6px;font-size:12.5px;line-height:1.5;color:var(--cd-navy,#14213d);display:flex;flex-direction:column;gap:2px}' +
      '.cd-line__pzk{color:var(--cd-muted,#5b6475)}' +
      '.cd-line__pzphoto{display:flex;align-items:center;gap:6px}' +
      '.cd-line__pzlink{display:inline-flex;align-items:center;gap:6px;text-decoration:underline;font-weight:600}' +
      '.cd-line__pzthumb{width:36px;height:36px;border-radius:6px;object-fit:cover;background:#faf7f2}' +
      '.cd-line__pzedit{display:inline-block;margin-top:4px;font-size:12.5px;font-weight:600;text-decoration:underline;text-underline-offset:2px;min-height:28px}';
    (document.head || document.documentElement).appendChild(st);
  })();

  function lineWrapHtml(it, partner, isWrap) {
    if (!partner) return '';
    if (isWrap) {
      var label = partner.product_title +
        (partner.variant_title && partner.variant_title !== 'Default Title' ? ' — ' + partner.variant_title : '');
      return '<p class="cd-line__wrap">For ' + esc(label) + '</p>';
    }
    /* The wrapping line itself shows the wrapped quantity and its charge;
       no extra status sentence is repeated under the gift. */
    return '';
  }

  /* A discount Shopify has already applied to this line (an automatic
     discount, or a code the customer applied to the cart). The line price
     above stays the original price; the discount is stated next to it and
     taken off once, in the total. */
  function lineDiscountHtml(it) {
    var amt = it.line_level_total_discount || 0;
    if (amt <= 0) return '';
    var titles = [];
    (it.line_level_discount_allocations || []).forEach(function (a) {
      var t = a && a.discount_application && a.discount_application.title;
      if (t && titles.indexOf(t) < 0) titles.push(t);
    });
    return '<p class="cd-line__disc">' + esc(titles.join(', ') || 'Discount') + ' −' + money(amt) + '</p>';
  }

  /* Discounts already in the Shopify cart, as rows the total can be checked
     against: original subtotal − these = c.total_price. */
  function cartDiscountRows(c) {
    var rows = [];
    if (!isCart(c) || !(c.total_discount > 0)) return rows;
    var lineLevel = 0;
    c.items.forEach(function (it) { lineLevel += it.line_level_total_discount || 0; });
    if (lineLevel > 0) rows.push({ title: 'Item discounts', amount: lineLevel });
    (c.cart_level_discount_applications || []).forEach(function (a) {
      if (a && a.total_allocated_amount > 0) rows.push({ title: a.title || 'Discount', amount: a.total_allocated_amount });
    });
    var listed = 0;
    rows.forEach(function (r) { listed += r.amount; });
    if (listed !== c.total_discount) rows.push({ title: 'Other discounts', amount: c.total_discount - listed });
    return rows;
  }

  /* THE Cadoo subtotal: the sum of it.original_line_price across every line
     the customer chose, add-ons and paid packaging included - the price
     total BEFORE any discount. A genuine product sale still shows correctly,
     because original_line_price tracks the variant's live selling price.

     Discounts are displayed, not entered: none of the cart surfaces (drawer,
     cart page, Delivery step, post-add modal) has a discount-code field;
     codes are entered only in Shopify's hosted checkout. When the Shopify
     cart already holds a discount, updateCartFoot() lists it beneath this
     subtotal and the estimated total is subtotal − cart.total_discount, which
     equals cart.total_price - the same reconciliation as
     sections/cadoo-cart.liquid and sections/cadoo-delivery-setup.liquid. */
  function cartSubtotal(c) {
    var sum = 0;
    if (c && c.items) {
      c.items.forEach(function (it) { sum += it.original_line_price; });
    }
    return sum;
  }

  /* What Shopify's free-delivery rate measures. The store's delivery rate
     for Lebanon is free when the order's TOTAL_PRICE is $90 or more
     (Settings > Shipping, verified against Shopify checkout): the cart total
     AFTER discounts, with every line counted - gifts, add-ons and paid
     packaging (gift wrapping, premium box). That is cart.total_price, i.e.
     the original subtotal minus cart.total_discount. Measuring the original
     subtotal instead promised free delivery that checkout then charged for
     whenever a discount took the total below the threshold.
     It is computed from the lines (original subtotal − total_discount, the
     same figure the drawer prints as the total), never read from
     c.total_price: after a quantity change the drawer holds Shopify's
     /cart/change.js answer, whose total_price can include an estimated
     delivery charge (seen: 9300 for an $89 cart that /cart.js reports as
     8900), which showed "free delivery unlocked" below $90. */
  function freeShipBasis(c) {
    if (!c || !c.items) return 0;
    return cartSubtotal(c) - (c.total_discount || 0);
  }

  /* THE Cadoo free-delivery rule, in one place.

     The threshold is the global theme setting settings.cadoo_free_ship_threshold,
     handed over on window.CadooConfig by layout/theme.liquid. The drawer footer
     and the post-add modal both render from this one result, so their progress
     bars and wording cannot drift apart.

     The Delivery & Gifting Details step reads that same global setting directly
     in Liquid (sections/cadoo-delivery-setup.liquid). It previously had a
     section-level threshold of its own that the Theme Editor could set
     independently; that setting has been removed, so there is now one source
     for this number across the drawer, the cart page, the modal and the
     delivery step. */
  function freeShipState(c) {
    var subtotal = freeShipBasis(c);
    var threshold = CFG.freeShipThreshold || 0;
    var hasItems = !!(c && c.items && c.items.length);

    if (!hasItems || !CFG.freeShipEnabled || threshold <= 0) {
      return {
        enabled: false, subtotal: subtotal, threshold: threshold,
        remaining: 0, percent: 0, unlocked: false,
        messageHtml: '', fillClass: 'cd-ship-bar__fill'
      };
    }

    var remaining = threshold - subtotal;
    var unlocked = remaining <= 0;
    return {
      enabled: true,
      subtotal: subtotal,
      threshold: threshold,
      remaining: unlocked ? 0 : remaining,
      percent: Math.max(0, Math.min(100, Math.round(subtotal / threshold * 100))),
      unlocked: unlocked,
      messageHtml: unlocked
        ? '<span class="cd-ship-bar__unlocked">🎉 You\'ve unlocked free delivery!</span>'
        : 'Add <strong>' + money(remaining) + '</strong> more for free delivery 🚚',
      fillClass: unlocked ? 'cd-ship-bar__fill cd-ship-bar__fill--complete' : 'cd-ship-bar__fill'
    };
  }

  /* Static footer controls (checkout form, view-bag link, total) live
     permanently in snippets/cadoo-drawers.liquid. This only flips [hidden]
     and updates text — it never touches the checkout form's markup, so
     Checkout cannot be lost by a re-render. */
  function updateCartFoot(c) {
    var totalRow = $('[data-cd-cart-totalrow]');
    var totalEl = $('[data-cd-cart-total]');
    var checkoutForm = $('[data-cd-cart-checkout]');
    var viewBag = $('[data-cd-cart-viewbag]');
    var shipMsg = $('[data-cd-cart-shipmsg]');
    var hasItems = !!(c && c.items && c.items.length);
    var subtotal = cartSubtotal(c);

    /* When Shopify's cart already carries a discount, the original subtotal,
       each discount and the discounted total are all shown, so the figures
       add up: Subtotal − discounts = Estimated total (= cart.total_price).
       With no discount this is exactly the old single total row. */
    var discounts = hasItems ? cartDiscountRows(c) : [];
    var discountTotal = 0;
    discounts.forEach(function (d) { discountTotal += d.amount; });
    var box = $('[data-cd-cart-discounts]');
    if (!box && totalRow && totalRow.parentNode) {
      box = document.createElement('div');
      box.className = 'cd-cart-discounts';
      box.setAttribute('data-cd-cart-discounts', '');
      totalRow.parentNode.insertBefore(box, totalRow);
    }
    if (box) {
      box.hidden = !discounts.length;
      box.innerHTML = discounts.length
        ? '<div class="cd-cart-discounts__row"><span>Subtotal</span><span>' + money(subtotal) + '</span></div>' +
          discounts.map(function (d) {
            return '<div class="cd-cart-discounts__row cd-cart-discounts__row--disc"><span>' + esc(d.title) +
              '</span><span>−' + money(d.amount) + '</span></div>';
          }).join('')
        : '';
    }
    if (totalEl) totalEl.textContent = money(subtotal - discountTotal);
    if (totalRow) totalRow.hidden = !hasItems;
    if (checkoutForm) checkoutForm.hidden = !hasItems;
    if (viewBag) viewBag.hidden = !hasItems;

    if (shipMsg) {
      var ship = freeShipState(c);
      shipMsg.innerHTML = ship.enabled
        ? '<div class="cd-ship-bar__text">' + ship.messageHtml + '</div>' +
          '<div class="cd-ship-bar__track"><div class="' + ship.fillClass +
          '" style="width:' + ship.percent + '%"></div></div>'
        : '';
    }
  }

  /* Paints the drawer from a cart object already in hand. Separated from
     renderCart() so a caller that has just refreshed the cart - the post-add
     modal, or a quantity change - can redraw without paying for a second
     /cart.js round trip. */
  function drawCart(c) {
      var body = $('[data-cd-cart-body]');
      if (!body) return;
      /* Only a real cart is drawn. Anything else (a failed read, an error
         response) leaves the last confirmed bag on screen; if nothing was
         ever confirmed, say the bag could not be loaded - never "empty". */
      if (!isCart(c)) {
        if (!cartState && !body.childNodes.length) {
          body.innerHTML = '<div class="cd-cart-empty" role="alert">' +
            '<h3 class="cd-cart-empty__title">We couldn’t load your bag.</h3>' +
            '<p class="cd-cart-empty__subtext">Please check your connection.</p>' +
            '<button type="button" class="cd-btn cd-btn--navy cd-cart-empty__cta" data-cd-cart-retry>Try again</button></div>';
        }
        return;
      }
      var focusKey = null, focusCtl = null;
      var active = document.activeElement;
      if (active && body.contains(active)) {
        var fl = active.closest('.cd-line');
        focusKey = fl && fl.getAttribute('data-key');
        focusCtl = active.hasAttribute('data-cd-qty') ? '[data-cd-qty="' + active.getAttribute('data-cd-qty') + '"]'
          : (active.hasAttribute('data-cd-remove') ? '[data-cd-remove]' : null);
      }
      if (!c.items.length) {
        body.innerHTML = '<div class="cd-cart-empty">' +
          '<span class="cd-cart-empty__icon" aria-hidden="true">' +
          '<svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">' +
          '<path d="M6 14.5h24v4.5H6z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>' +
          '<path d="M8 19h20v11.5a1.5 1.5 0 0 1-1.5 1.5h-17A1.5 1.5 0 0 1 8 30.5V19z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>' +
          '<path d="M18 14.5V32" stroke="currentColor" stroke-width="1.4"/>' +
          '<path d="M18 14.5c0-3.4-2.6-6-5.3-6a2.9 2.9 0 0 0 0 5.8c2.4 0 5.3.2 5.3.2z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>' +
          '<path d="M18 14.5c0-3.4 2.6-6 5.3-6a2.9 2.9 0 0 1 0 5.8c-2.4 0-5.3.2-5.3.2z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>' +
          '<path d="M18 23.6c-1-1.3-2.9-1-2.9.6 0 1.5 2.9 3.1 2.9 3.1s2.9-1.6 2.9-3.1c0-1.6-1.9-1.9-2.9-.6z" fill="#F47C5C"/>' +
          '</svg>' +
          '</span>' +
          '<h3 class="cd-cart-empty__title">Your bag is empty.</h3>' +
          '<p class="cd-cart-empty__subtext">Let’s find you something.</p>' +
          '<a href="/pages/gift-finder" class="cd-btn cd-btn--navy cd-cart-empty__cta">Find a gift</a>' +
          '<a href="' + (CFG.routes.all || '/collections/all') + '" class="cd-link cd-cart-empty__link">Browse all gifts <span aria-hidden="true">→</span></a>' +
          '</div>';
        updateCartFoot(c);
        if (focusKey) restoreLineFocus(body, focusKey, focusCtl);
        return;
      }
      /* Every line the customer chose is drawn, add-ons included. Nothing is
         suppressed, so the bag always shows exactly what will be charged. */
      var html = '';
      c.items.forEach(function (it, i) { html += renderLine(it, i, c); });
      body.innerHTML = html;
      updateCartFoot(c);
      if (focusKey) restoreLineFocus(body, focusKey, focusCtl);
  }

  /* innerHTML replaces the control the customer was using; put keyboard and
     screen-reader focus back on the same control, or on the nearest line if
     that line was removed. */
  function restoreLineFocus(body, key, ctl) {
    var lines = $$('.cd-line', body);
    var target = null;
    lines.some(function (el) {
      if (el.getAttribute('data-key') !== key) return false;
      target = (ctl && el.querySelector(ctl)) || el.querySelector('[data-cd-qty]');
      return true;
    });
    if (!target && lines.length) target = lines[0].querySelector('[data-cd-qty]');
    if (!target) target = $('#cd-cart [data-cd-close]');
    if (target && target.focus) { try { target.focus(); } catch (e) {} }
  }

  /* Opening the drawer paints the last confirmed bag at once, then re-reads
     the cart and its quantity limits together and repaints. */
  function renderCart() {
    if (!$('[data-cd-cart-body]')) return;
    if (cartState) drawCart(cartState);
    Promise.all([getCart(), fetchLimits()]).then(function (r) { drawCart(r[0] || cartState); });
  }

  /* Refetches the real cart and redraws every cart surface from it. Returns
     the cart so a caller can read its own totals from the same object; if
     the read fails, the last confirmed cart is returned and kept on screen. */
  function refreshCart() {
    return Promise.all([getCart(), fetchLimits()]).then(function (r) {
      var c = r[0] || cartState;
      drawCart(c);
      return c;
    });
  }

  document.addEventListener('cadoo:panel-closed', function (e) {
    if (e.detail && e.detail.panel && e.detail.panel.id === 'cd-cart') lineNotes = Object.create(null);
  });

  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[data-cd-cart-retry]')) { renderCart(); return; }
    var q = e.target.closest && e.target.closest('[data-cd-qty]');
    if (q && q.closest('.cd-line')) {
      var line2 = q.closest('.cd-line');
      e.preventDefault();
      if (cartBusy) return;                       /* one update at a time */
      var cur = lineByKey(cartState, line2.getAttribute('data-key'));
      if (!cur) { renderCart(); return; }
      if (q.getAttribute('aria-disabled') === 'true') {
        /* Kept focusable so keyboard users are not dropped; just explain. */
        var lim = lineLimit(cur, cartState);
        if (cur.handle === WRAP_HANDLE && !limitNote(lim)) {
          announce('Every unit of this gift is already wrapped.');
          return;
        }
        announce(limitNote(lim) || 'That quantity is not available.');
        return;
      }
      var step = lineLimit(cur, cartState).step;
      var dir = parseInt(q.getAttribute('data-cd-qty'), 10) < 0 ? -1 : 1;
      changeLine(cur.key, Math.max(0, cur.quantity + dir * step));
      return;
    }
    var r = e.target.closest && e.target.closest('[data-cd-remove]');
    if (r && r.closest('.cd-line')) {
      e.preventDefault();
      if (cartBusy) return;
      changeLine(r.closest('.cd-line').getAttribute('data-key'), 0);
    }
  });

  /* ---------------- ADD-TO-BAG confirmation ----------------
     A Main Gift opens the post-add confirmation modal
     (assets/cadoo-addon-modal.js). Everything else - an add-on bought on its
     own, or any add at all if the modal script is unavailable - falls back to
     the one real cart drawer with a small banner, which already shows the
     correct combined total.

     The old "Complete the gift" cross-sell that used to load into this banner
     from Shopify's complementary recommendations has been removed. Approval
     now comes from custom.compatible_add_ons alone, and it is presented in the
     modal - a recommendation engine can no longer put a product in front of a
     customer that an administrator never approved. */
  var bannerTimer = null;

  function hideAddedBanner() {
    clearTimeout(bannerTimer);
    var banner = $('[data-cd-added-banner]');
    if (banner) banner.hidden = true;
  }

  function showCartBanner(text) {
    var banner = $('[data-cd-added-banner]');
    if (!banner) return null;
    banner.hidden = false;
    var textEl = $('[data-cd-added-banner-text]', banner);
    if (textEl) textEl.textContent = text;
    clearTimeout(bannerTimer);
    bannerTimer = setTimeout(hideAddedBanner, 6000);
    return banner;
  }

  function scrollToLine(item) {
    var key = item && item.key;
    if (!key) return;
    setTimeout(function () {
      var line = document.querySelector('.cd-line[data-key="' + key + '"]');
      if (line && line.scrollIntoView) line.scrollIntoView({ block: 'nearest' });
    }, 80);
  }

  function openCartConfirmation(item) {
    var panel = $('#cd-cart');
    if (!panel) return;
    showCartBanner('Added to your gift bag');
    openPanel(panel);
    scrollToLine(item);
  }

  /* THE ONE SUCCESS PATH, for every entry point.

     By the time this runs the add has already succeeded and getCart() has
     already refreshed cartState, so the badge and the drawer markup are
     current even though the drawer itself stays shut.

     Which confirmation appears is decided by the originating product's role,
     read from custom.product_role and carried on the markup that started the
     add - never inferred from a title, a handle or a collection:

       Main Gift / Both, added on its own    -> the post-add modal
       Add-on, added on its own              -> the cart drawer, as before
       role not knowable from the markup     -> ask the offer section, which
                                                reports the role it read from
                                                the product itself
       modal script unavailable              -> the cart drawer

     An add-on accepted inside the modal never reaches this function, so the
     modal can never reopen or stack on itself. */
  function afterAdd(item, info) {
    info = info || {};
    drawCart(cartState);
    fetchLimits().then(function (ok) { if (ok && cartState) drawCart(cartState); });

    var modal = window.CadooAddonModal;
    var fallback = function () {
      closeAll();
      openCartConfirmation(item);
    };

    if (!modal) {
      fallback();
      return Promise.resolve();
    }

    if (info.roleKnown) {
      if (modal.isMainGiftRole(info.role) && modal.open(item, info)) return Promise.resolve();
      fallback();
      return Promise.resolve();
    }

    return modal.openDeferred(item, info, fallback);
  }

  /* ---------------- QUICK ADD ----------------
     One delegated listener covering the shared product card, the Saved drawer
     and the Saved page. The lock belongs to this whole request chain, not to
     one intermediate callback: the add, cart refresh and confirmation flow
     must all finish before the control can be used again. The variant lock
     also covers duplicate card instances on a dynamically-rendered Saved page. */
  var quickAddFlights = Object.create(null);

  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('[data-cd-quickadd]');
    if (!b) return;
    if (b.disabled || b.__cdQuickAddPending) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    e.preventDefault();
    e.stopImmediatePropagation();
    var id = b.getAttribute('data-cd-quickadd');
    if (!id || quickAddFlights[id]) return;

    var label = b.innerHTML;
    b.__cdQuickAddPending = true;
    quickAddFlights[id] = true;
    b.disabled = true;
    b.setAttribute('aria-busy', 'true');
    b.setAttribute('aria-disabled', 'true');
    b.innerHTML = 'ADDING…';

    addToCart({ items: [{ id: Number(id), quantity: 1 }] })
      .then(function (d) {
        var item = (d.items && d.items[0]) || d;
        return getCart().then(function () {
          return afterAdd(item, {
            role: b.getAttribute('data-cd-role') || '',
            roleKnown: b.hasAttribute('data-cd-role'),
            url: b.getAttribute('data-cd-url') || (item && item.url) || '',
            productId: b.getAttribute('data-cd-product-id') || (item && item.product_id) || null,
            productTitle: b.getAttribute('data-cd-product-title') || (item && item.product_title) || '',
            source: b.getAttribute('data-cd-source') || 'quick-add',
            initiator: b
          });
        });
      })
      .catch(function (err) {
        toast((err && err.message) || 'We could not add that to your bag. Please try again.');
        /* Shopify may have added part of the request (e.g. "Only 1 item was
           added... due to availability"); bring the badge and bag in line. */
        return refreshCart();
      })
      .then(function () {
        b.__cdQuickAddPending = false;
        delete quickAddFlights[id];
        b.disabled = false;
        b.removeAttribute('aria-busy');
        b.removeAttribute('aria-disabled');
        b.innerHTML = label;
      });
  }, true);

  /* ==========================================================================
     PDP — sections/cadoo-product.liquid
     Scoped per [data-cd-pdp] root so a featured-product section and the main
     product section can coexist without clashing. Product-level gifting
     (the "Make it a gift" module, premium presentation, per-product message
     fields) has been retired — Add to Cart sends a single plain line item,
     with no gift properties. Every order is presented the same way by
     default; the one order-level message card is collected on the Delivery
     Setup step instead.
     ========================================================================== */
  function readJson(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  /* ---------------- mobile gallery (native scroll-snap) ----------------
     Desktop's swap-in-place main image (data-cd-pdp-main, slide 1) is left
     completely alone. This only wires the mobile track: syncing the 1 / N
     counter and the desktop thumbnail aria-current state to whichever slide
     is scrolled into view, and exposing a scrollToMedia() helper the
     existing thumb-click handler calls so a variant change (which already
     drives thumb.click()) also moves the mobile slide - no separate code
     path, so the two can never disagree about which image is showing. */
  function initGallery(root) {
    var track = $('[data-cd-pdp-track]', root);
    if (!track) return;
    var slides = $$('[data-cd-pdp-slide]', track);
    if (slides.length < 2) return;

    var counterCur = $('[data-cd-pdp-counter-cur]', root);
    var thumbs = $$('[data-cd-pdp-thumb]', root);

    /* Matches scroll position to the nearest slide by its own offsetLeft
       rather than dividing by track.clientWidth. A "peek" gallery narrows
       each slide (see .cd-pdp__gallery--peek in cadoo-pdp.css), so the
       distance between slides is no longer the same as the track's width -
       comparing real slide offsets keeps this correct either way. */
    function indexFromScroll() {
      var pos = track.scrollLeft;
      var best = 0, bestDist = Infinity;
      for (var i = 0; i < slides.length; i++) {
        var d = Math.abs(slides[i].offsetLeft - pos);
        if (d < bestDist) { bestDist = d; best = i; }
      }
      return best;
    }

    function syncFromScroll() {
      var idx = indexFromScroll();
      if (counterCur) counterCur.textContent = String(idx + 1);
      thumbs.forEach(function (t, i) { t.setAttribute('aria-current', i === idx ? 'true' : 'false'); });
    }

    var raf = null;
    track.addEventListener('scroll', function () {
      if (raf) return;
      raf = window.requestAnimationFrame(function () { raf = null; syncFromScroll(); });
    }, { passive: true });

    track.__cdScrollToMedia = function (mediaId) {
      var idx = -1;
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].getAttribute('data-media-id') === String(mediaId)) { idx = i; break; }
      }
      if (idx < 0) return;
      var slide = slides[idx];
      /* Only the mobile track actually scrolls (desktop's track is a
         non-scrolling block per CSS), so this is a no-op on desktop
         viewports - it just repositions state that isn't visible there. */
      track.scrollTo({ left: slide.offsetLeft, behavior: 'auto' });
      if (counterCur) counterCur.textContent = String(idx + 1);
    };
  }

  function setupPdp(root) {
    var data = readJson($('[data-cd-pdp-data]', root)) || {};
    var variants = data.variants || [];
    var addLabel = data.addLabel || 'Add to cart';
    var soldLabel = data.soldOutLabel || 'Sold out';

    var form = $('[data-cd-product-form]', root);
    var idInput = $('[data-cd-variant-id]', root);
    var qtyInput = $('[data-cd-qty-input]', root);
    var qtyBox = $('[data-cd-qty]', root);
    var priceWrap = $('[data-cd-price]', root);
    var priceNow = $('[data-cd-price-now]', root);
    var availState = $('[data-cd-avail-state]', root);
    var availSep = $('[data-cd-avail-sep]', root);
    var availDel = $('[data-cd-avail-delivery]', root);
    var submitBtn = $('[data-cd-submit]', root);
    var submitLabel = $('[data-cd-submit-label]', root);
    var totalEl = $('[data-cd-total]', root);
    var errEl = $('[data-cd-err]', root);
    var stickyBar = $('[data-cd-sticky]', root);
    var stickyBtn = $('[data-cd-sticky-btn]', root);
    var stickyTotal = $('[data-cd-sticky-total]', root);
    /* The mobile sticky bar shows the selected variant's UNIT price (its
       server-rendered markup in sections/cadoo-product.liquid is a plain
       price under the title); the quantity total stays on the main button. */
    var stickyPrice = $('[data-cd-sticky-price]', root);

    var current = null;
    var busy = false;

    function variantById(id) {
      for (var i = 0; i < variants.length; i++) { if (variants[i].id === id) return variants[i]; }
      return null;
    }

    /* Reads one value per option GROUP (the [data-cd-opt] wrapper for that
       option index), not per input — a group renders either a set of radio
       pills/swatches (Rule A/B) or a single <select> (Rule C, 5+ values;
       see sections/cadoo-product.liquid). Grouping by [data-cd-opt] keeps
       the returned array correctly ordered by option index either way, and
       a <select>'s .value is always "the current choice" with no .checked
       concept to filter on. */
    function selectedValues() {
      return $$('[data-cd-opt]', root).map(function (g) {
        var sel = g.querySelector('select[data-cd-opt-input]');
        if (sel) return sel.value;
        var checked = $$('[data-cd-opt-input]', g).filter(function (i) { return i.checked; })[0];
        return checked ? checked.value : null;
      });
    }

    function matchVariant(vals) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i], ok = true;
        for (var j = 0; j < vals.length; j++) {
          if (v.options[j] !== vals[j]) { ok = false; break; }
        }
        if (ok) return v;
      }
      return null;
    }

    /* Same availability rule for both control types: a value is available
       when at least one variant matching it (plus every OTHER currently
       selected value) is in stock. Pills/swatches get the existing
       .is-unavailable class; a <select>'s individual <option>s get native
       [disabled] instead, since that's the correct greyed-out affordance
       for a dropdown. */
    function markAvailability(vals) {
      function isAvailable(idx, val) {
        return variants.some(function (v) {
          if (v.options[idx] !== val) return false;
          for (var j = 0; j < vals.length; j++) {
            if (j !== idx && v.options[j] !== vals[j]) return false;
          }
          return !!v.available;
        });
      }
      $$('[data-cd-opt-item]', root).forEach(function (item) {
        var idx = parseInt(item.getAttribute('data-index'), 10);
        var val = item.getAttribute('data-value');
        item.classList.toggle('is-unavailable', !isAvailable(idx, val));
      });
      $$('select[data-cd-opt-input]', root).forEach(function (sel) {
        var idx = parseInt(sel.getAttribute('data-index'), 10);
        $$('option[data-value]', sel).forEach(function (opt) {
          opt.disabled = !isAvailable(idx, opt.getAttribute('data-value'));
        });
      });
    }

    function qty() {
      var n = parseInt(qtyInput && qtyInput.value, 10);
      return (isNaN(n) || n < 1) ? 1 : n;
    }

    function renderTotal() {
      var txt = '';
      if (current && current.available) {
        var cents = current.price * qty();
        txt = ' · ' + money(cents);
      }
      if (totalEl) totalEl.textContent = txt;
      if (stickyTotal) stickyTotal.textContent = txt;
    }

    function clampQty() {
      if (!qtyInput) return;
      var max = parseInt(qtyInput.getAttribute('max'), 10);
      var n = qty();
      if (!isNaN(max) && max > 0 && n > max) n = max;
      qtyInput.value = n;
      $$('[data-cd-qty-step]', root).forEach(function (b) {
        var st = parseInt(b.getAttribute('data-cd-qty-step'), 10);
        b.disabled = (st < 0 && n <= 1) || (st > 0 && !isNaN(max) && max > 0 && n >= max);
      });
    }

    function setMedia(v) {
      if (!v) return;

      function mediaKey(value) {
        return String(value || '').split('/').pop();
      }

      var mediaId = mediaKey(v.media);
      var thumb = null;
      var slide = null;

      $$('[data-cd-pdp-thumb]', root).some(function (el) {
        if (mediaKey(el.getAttribute('data-media-id')) === mediaId) {
          thumb = el;
          return true;
        }
        return false;
      });

      $$('[data-cd-pdp-slide]', root).some(function (el) {
        if (mediaKey(el.getAttribute('data-media-id')) === mediaId) {
          slide = el;
          return true;
        }
        return false;
      });

      if (thumb) {
        thumb.click();
      }

      var track = $('[data-cd-pdp-track]', root);

      if (track && slide) {
        track.scrollLeft = slide.offsetLeft;

        if (track.__cdScrollToMedia) {
          track.__cdScrollToMedia(slide.getAttribute('data-media-id'));
        }
      }

      if (!thumb && v.image) {
        var main = $('[data-cd-pdp-main]', root);

        if (main) {
          main.removeAttribute('srcset');
          main.removeAttribute('sizes');
          main.src = v.image;
        }
      }
    }


    function applyVariant(v, pushUrl) {
      current = v;
      if (idInput) idInput.value = v ? v.id : '';

      if (priceNow) priceNow.textContent = v ? money(v.price) : '';
      if (stickyPrice) stickyPrice.textContent = v ? money(v.price) : '';
      if (priceWrap) {
        var s = priceWrap.querySelector('s');
        if (v && v.compare > v.price) {
          if (!s) { s = document.createElement('s'); priceWrap.insertBefore(s, priceWrap.firstChild); }
          s.textContent = money(v.compare);
          s.hidden = false;
        } else if (s) { s.hidden = true; }
      }

      var avail = !!(v && v.available);
      if (availState) {
        availState.classList.toggle('is-in', avail);
        availState.classList.toggle('is-out', !avail);
        availState.innerHTML = '<span class="cd-p-avail__dot" aria-hidden="true"></span>';
        availState.appendChild(document.createTextNode(avail ? 'In stock' : soldLabel));
      }
      var showDelivery = avail && !!data.deliveryMessage;
      if (availSep) availSep.hidden = !showDelivery;
      if (availDel) availDel.hidden = !showDelivery;

      if (submitBtn) submitBtn.disabled = !avail;
      if (submitLabel) {
        /* Editing a personalized bag line keeps its "Save changes" label. */
        var editing = root.hasAttribute('data-cd-pz-editing');
        submitLabel.textContent = avail ? (editing ? 'Save changes' : addLabel) : (v ? soldLabel : 'Unavailable');
      }
      if (stickyBtn) stickyBtn.disabled = !avail;

      $$('[data-cd-opt]', root).forEach(function (g) {
        var idx = parseInt(g.getAttribute('data-index'), 10);
        var lab = $('[data-cd-opt-selected]', g);
        var selectedValue = v && v.options ? v.options[idx] : null;

        if (lab) {
          lab.textContent = selectedValue || '';
        }

        $$('[data-cd-opt-item]', g).forEach(function (item) {
          var input = item.querySelector('[data-cd-opt-input]');
          var selected = !!(
            input &&
            selectedValue &&
            input.value === selectedValue
          );

          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-current', selected ? 'true' : 'false');

          if (input) {
            input.checked = selected;
            input.setAttribute('aria-checked', selected ? 'true' : 'false');
          }
        });
      });


      if (qtyInput) {
        if (v && v.qmax != null && v.qmax > 0) qtyInput.setAttribute('max', v.qmax);
        else qtyInput.removeAttribute('max');
        clampQty();
      }

      if (pushUrl && v && window.history && window.history.replaceState) {
        try {
          var u = new URL(window.location.href);
          u.searchParams.set('variant', String(v.id));
          window.history.replaceState({ cadooVariant: v.id }, '', u.toString());
        } catch (e) {}
      }

      /* The chosen option changed (a picker change, a history restore, or
         the initial preselection). Anything that depends on the current
         variant - the Secret Santa heart, which saves the exact option -
         re-evaluates on this, without reaching into this closure. */
      try {
        document.dispatchEvent(new CustomEvent('cadoo:variant', {
          detail: { id: v ? String(v.id) : null, available: !!(v && v.available) }
        }));
      } catch (e) {}
    }

    /* Origin data for the success path, read from the section's own markup:
       the role Liquid printed from custom.product_role, and the product's own
       localized URL. Never derived from a title, a handle or a collection. */
    function pdpOrigin(trigger) {
      var role = (root.getAttribute('data-cd-role') || '').trim();
      return {
        role: role,
        /* An empty attribute is not a known role. Let the offer section read
           the product metafield server-side instead of falling straight back
           to the cart drawer. */
        roleKnown: !!role,
        url: root.getAttribute('data-cd-url') || '',
        productId: root.getAttribute('data-cd-product-id') || null,
        productTitle: root.getAttribute('data-cd-product-title') || '',
        source: trigger === stickyBtn ? 'sticky' : 'pdp',
        initiator: trigger || submitBtn
      };
    }

    /* The standard button and the mobile sticky button are the same path: one
       function, one closure, one busy flag, and both controls disabled
       together, so the two can never double-submit between them. */
    function doAdd(trigger) {
      if (busy || !current || !current.available) return;

      /* PERSONALIZED GIFTS: a product with personalization fields hands the
         request to assets/cadoo-personalize.js, which validates the required
         name/photo first and then posts the real form as multipart FormData
         (the file must travel as a File, not JSON). Normal products never
         reach this branch and keep the plain JSON add below. */
      var pz = (window.CadooPersonalize && window.CadooPersonalize.forRoot) ? window.CadooPersonalize.forRoot(root) : null;
      if (pz && !pz.validate().ok) return;

      busy = true;
      if (errEl) { errEl.hidden = true; errEl.textContent = ''; }
      if (submitBtn) { submitBtn.disabled = true; submitBtn.setAttribute('aria-busy', 'true'); }
      if (stickyBtn) stickyBtn.disabled = true;
      if (submitLabel) submitLabel.textContent = pz ? pz.busyLabel() : 'Adding…';
      if (totalEl) totalEl.textContent = '';
      if (stickyTotal) stickyTotal.textContent = '';

      var n = qty();
      var items = [{ id: current.id, quantity: n }];

      var request = pz ? pz.submit(current.id, n) : addToCart({ items: items });
      request
        .then(function (d) {
          var item = pz ? d : ((d.items && d.items[0]) || d);
          if (pz && pz.isEdit()) {
            /* Saved an existing bag line: back to the bag, no post-add modal. */
            pz.afterEditSaved();
            return new Promise(function () {}); /* navigation in progress */
          }
          return getCart().then(function () { return afterAdd(item, pdpOrigin(trigger)); });
        })
        .catch(function (err) {
          /* The add failed, so nothing is confirmed and no modal opens. The
             existing inline PDP error is preserved exactly as it was. */
          if (errEl) { errEl.textContent = (err && err.message) || 'Could not add to bag.'; errEl.hidden = false; }
          else toast((err && err.message) || 'Could not add to bag.');
          /* A rejected add can still be a partial add ("Only 1 item was added
             to your cart due to availability"), so re-read the real cart to
             keep the header count and the drawer in step with it. */
          return refreshCart();
        })
        .then(function () {
          busy = false;
          if (submitBtn) { submitBtn.disabled = false; submitBtn.removeAttribute('aria-busy'); }
          if (stickyBtn) stickyBtn.disabled = false;
          if (submitLabel) submitLabel.textContent = (pz && pz.ctaLabel()) || addLabel;
          renderTotal();
        });
    }

    /* Expose the current form's custom submit path to the document-level
       safety net below. This is deliberately assigned to the current setup,
       so a replaced form never reuses stale product controls. */
    root.__cdPdpForm = form;
    root.__cdPdpSubmit = doAdd;

    /* ---- wiring (one delegated listener per event, per root) ---- */
    root.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.matches) return;
      if (t.matches('[data-cd-opt-input]')) {
        var vals = selectedValues();
        markAvailability(vals);
        var v = matchVariant(vals);
        applyVariant(v, true);
        setMedia(v);
        renderTotal();
      } else if (t.matches('[data-cd-qty-input]')) {
        clampQty();
        renderTotal();
      }
    });

    root.addEventListener('input', function (e) {
      var t = e.target;
      if (!t || !t.matches) return;
      if (t.matches('[data-cd-qty-input]')) renderTotal();
    });

    if (qtyBox) {
      $$('[data-cd-qty-step]', qtyBox).forEach(function (b) {
        b.addEventListener('click', function () {
          if (!qtyInput) return;
          qtyInput.value = qty() + parseInt(b.getAttribute('data-cd-qty-step'), 10);
          clampQty();
          renderTotal();
        });
      });
    }

    initGallery(root);

    $$('[data-cd-pdp-thumb]', root).forEach(function (t) {
      t.addEventListener('click', function () {
        var main = $('[data-cd-pdp-main]', root);
        if (main) {
          /* main renders with a srcset (600/900/1200/1600w) for the FIRST
             image. Once srcset is present the browser resolves the shown
             image from srcset + sizes, not from src - so writing main.src
             alone is silently ignored on every thumbnail except whichever
             one already matches what srcset was already resolving to. Clear
             srcset/sizes so the plain src we just set is what actually
             renders; data-full is a single 1200w image, which matches what
             the old srcset would have picked at desktop widths anyway. */
          main.removeAttribute('srcset');
          main.removeAttribute('sizes');
          main.src = t.getAttribute('data-full');
          main.alt = t.getAttribute('data-alt') || '';
        }
        $$('[data-cd-pdp-thumb]', root).forEach(function (x) { x.setAttribute('aria-current', 'false'); });
        t.setAttribute('aria-current', 'true');
        /* Keep the mobile scroll-snap track (and its counter) synchronized
           with whatever just became the selected image - covers both a
           direct thumbnail click and setMedia()'s thumb.click() call on
           variant change, since they share this one handler. */
        var track = $('[data-cd-pdp-track]', root);
        if (track && track.__cdScrollToMedia) track.__cdScrollToMedia(t.getAttribute('data-media-id'));
      });
    });

    if (form) {
      form.addEventListener('submit', function (e) { e.preventDefault(); doAdd(submitBtn); });
    }
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function (e) { e.preventDefault(); doAdd(stickyBtn); });
    }

    if (stickyBar && submitBtn && typeof window.IntersectionObserver === 'function') {
      document.body.classList.add('cd-has-stickycta');
      var io = new window.IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          var on = !en.isIntersecting && en.boundingClientRect.top < 0;
          stickyBar.classList.toggle('is-on', on);
          stickyBar.setAttribute('aria-hidden', on ? 'false' : 'true');
          if (stickyBtn) stickyBtn.tabIndex = on ? 0 : -1;
        });
      }, { threshold: 0 });
      io.observe(submitBtn);
    }

    var panel = $('[data-cd-sticky-panel]', root);
    if (panel) {
      var syncStickyPanel = function () {
        panel.classList.toggle(
          'is-sticky',
          window.innerWidth >= 1200 && panel.offsetHeight < (window.innerHeight - 140)
        );
      };
      syncStickyPanel();
      window.addEventListener('resize', syncStickyPanel);
    }

    window.addEventListener('popstate', function () {
      var raw = null;
      try { raw = new URL(window.location.href).searchParams.get('variant'); } catch (e) { raw = null; }
      if (!raw) return;
      var v = variantById(Number(raw));
      if (!v) return;
      $$('[data-cd-opt-input]', root).forEach(function (i) {
        var idx = parseInt(i.getAttribute('data-index'), 10);
        if (i.tagName === 'SELECT') { i.value = v.options[idx]; }
        else { i.checked = (v.options[idx] === i.value); }
      });
      markAvailability(v.options);
      applyVariant(v, false);
      setMedia(v);
      renderTotal();
    });

    /* ---- first paint ---- */
    var startVals = selectedValues();
    var start = matchVariant(startVals);
    if (!start && idInput) start = variantById(Number(idInput.value));
    if (!start) start = variants[0] || null;
    markAvailability(startVals);
    applyVariant(start, false);
    setMedia(start);
    clampQty();
    renderTotal();
  }

  function initPdp() {
    $$('[data-cd-pdp]').forEach(function (root) {
      var form = $('[data-cd-product-form]', root);
      if (!form) return;
      if (root.__cdPdp && root.__cdPdpForm === form) return;

      /* The root can survive a Shopify morph while its form is replaced. Do
         not keep the old setup marker in that case, or the new form would
         silently fall back to the native cart redirect. */
      root.__cdPdp = 0;
      root.__cdPdpForm = null;
      root.__cdPdpSubmit = null;
      setupPdp(root);
      root.__cdPdp = 1;
    });
  }

  /* Capture the current product form's submit before the browser can perform
     Shopify's native navigation. If a section was hydrated after the initial
     page setup, initialize that exact form on demand and send it through the
     same doAdd path as the normal listener. */
  document.addEventListener('submit', function (e) {
    var form = e.target && e.target.closest && e.target.closest('[data-cd-product-form]');
    if (!form) return;
    var root = form.closest('[data-cd-pdp]');
    if (!root) return;

    initPdp();
    if (root.__cdPdpForm !== form) {
      root.__cdPdp = 0;
      initPdp();
    }

    if (root.__cdPdpSubmit) {
      e.preventDefault();
      e.stopImmediatePropagation();
      root.__cdPdpSubmit($('[data-cd-submit]', root));
    }
  }, true);

  /* Shopify's Theme Editor can replace a section after this global script has
     already initialized. Re-run the guarded PDP initializer so a newly
     rendered product form keeps the custom add-to-cart path. */
  document.addEventListener('shopify:section:load', function () {
    initPdp();
  });

function initSearch() {
  var panel = $('#cd-search');
  if (!panel) return;

  var input = $('[data-cd-search-input]', panel);
  var results = $('[data-cd-search-results]', panel);
  var intents = $('[data-cd-search-intents]', panel);
  var initial = $('[data-cd-search-initial]', panel);

  if (!input || !results || !initial) return;

  var timer = null;
  var controller = null;

  var intentMap = [
    {
      group: 'Occasions',
      label: 'Congratulations',
      search: 'congratulations',
      keywords: [
        'congratulations',
        'congrats',
        'well done',
        'you did it'
      ],
      url: '/collections/all-gifts-1?filter.p.m.custom.occasion=gid%3A%2F%2Fshopify%2FMetaobject%2F313246154845'
    },
    {
      group: 'Occasions',
      label: 'Birthday',
      search: 'birthday',
      keywords: [
        'birthday',
        'birthday gift',
        'birthday present'
      ],
      url: '/collections/all-gifts-1?filter.p.m.custom.occasion=gid%3A%2F%2Fshopify%2FMetaobject%2F313245958237'
    },
    {
      group: 'Occasions',
      label: 'Housewarming',
      search: 'housewarming',
      keywords: [
        'housewarming',
        'new home',
        'new apartment',
        'new house'
      ],
      url: '/collections/all-gifts-1?filter.p.m.custom.occasion=gid%3A%2F%2Fshopify%2FMetaobject%2F313245991005'
    },
    {
      group: 'Occasions',
      label: 'Just Because',
      search: 'just because',
      keywords: [
        'just because',
        'thinking of you',
        'thinking about you',
        'no occasion'
      ],
      url: '/collections/all-gifts-1?filter.p.m.custom.occasion=gid%3A%2F%2Fshopify%2FMetaobject%2F313246187613'
    },
    {
      group: 'Interests',
      label: 'Home & Living',
      search: 'home decor',
      keywords: [
        'home decor',
        'home décor',
        'home and decor',
        'home gifts',
        'decor gifts',
        'new home'
      ],
      url: '/collections/all-gifts-1?filter.p.m.custom.interest=gid%3A%2F%2Fshopify%2FMetaobject%2F313245466717'
    },
    {
      group: 'Interests',
      label: 'Kids & Learning',
      search: 'educational toys',
      keywords: [
        'educational toys',
        'learning toys',
        'educational gift',
        'learning gift',
        'toys for kids',
        'kids toys'
      ],
      url: '/collections/all-gifts-1?filter.p.m.custom.interest=gid%3A%2F%2Fshopify%2FMetaobject%2F313245597789'
    },
    {
      group: 'Interests',
      label: 'Games & Entertainment',
      search: 'games',
      keywords: [
        'games',
        'board games',
        'fun gifts',
        'entertainment'
      ],
      url: '/collections/all-gifts-1?filter.p.m.custom.interest=gid%3A%2F%2Fshopify%2FMetaobject%2F313245630557'
    },
    {
      group: 'Interests',
      label: 'Bar & Hosting',
      search: 'bar hosting',
      keywords: [
        'bar hosting',
        'bar gifts',
        'hosting gifts',
        'cocktail gifts',
        'wine gifts',
        'whiskey gifts',
        'drinks'
      ],
      url: '/collections/all-gifts-1?filter.p.m.custom.interest=gid%3A%2F%2Fshopify%2FMetaobject%2F313245532253'
    },
    {
      group: 'Interests',
      label: 'Fitness & Wellness',
      search: 'fitness wellness',
      keywords: [
        'fitness',
        'gym gifts',
        'wellness',
        'self care',
        'self-care'
      ],
      url: '/collections/all-gifts-1?filter.p.m.custom.interest=gid%3A%2F%2Fshopify%2FMetaobject%2F313245565021'
    },
    {
      group: 'Recipients',
      label: 'For the Host',
      search: 'host',
      keywords: [
        'host',
        'hostess',
        'for the host',
        'host gift'
      ],
      url: '/collections/all-gifts-1?filter.p.m.custom.recipient=gid%3A%2F%2Fshopify%2FMetaobject%2F313245433949'
    },
    {
      group: 'Recipients',
      label: 'For Him',
      search: 'for him',
      keywords: [
        'for him',
        'gifts for him',
        'gift for a man',
        'gifts for men'
      ],
      url: '/collections/all-gifts-1?filter.p.m.custom.recipient=gid%3A%2F%2Fshopify%2FMetaobject%2F313245204573'
    },
    {
      group: 'Recipients',
      label: 'For Her',
      search: 'for her',
      keywords: [
        'for her',
        'gifts for her',
        'gift for a woman',
        'gifts for women'
      ],
      url: '/collections/all-gifts-1?filter.p.m.custom.recipient=gid%3A%2F%2Fshopify%2FMetaobject%2F313245171805'
    },
    {
      group: 'Budget',
      label: 'Gifts under $25',
      search: 'gifts under 25',
      keywords: [
        'under 25',
        'under $25',
        'below 25',
        'cheap gifts',
        'small gifts',
        'affordable gifts'
      ],
      url: '/collections/all-gifts-1?filter.v.price.lte=25'
    }
  ];

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[’']/g, '')
      .replace(/[$€£]/g, '')
      .replace(/[^\w\s&-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function findIntent(query) {
    var normalized = normalize(query);
    if (!normalized) return null;

    var matches = intentMap.filter(function (intent) {
      return intent.keywords.some(function (keyword) {
        var phrase = normalize(keyword);
        return normalized === phrase || normalized.indexOf(phrase) !== -1;
      });
    });

    if (!matches.length) return null;

    matches.sort(function (a, b) {
      var aLength = Math.max.apply(null, a.keywords.map(function (word) {
        return normalize(word).length;
      }));
      var bLength = Math.max.apply(null, b.keywords.map(function (word) {
        return normalize(word).length;
      }));
      return bLength - aLength;
    });

    return matches[0];
  }

  function renderIntent(intent) {
    if (!intents) return;

    if (!intent) {
      intents.innerHTML = '';
      return;
    }

    intents.innerHTML =
      '<div class="cd-search-intent">' +
        '<p class="cd-eyebrow" style="margin:0 0 8px">' +
          escapeHtml(intent.group) +
        '</p>' +
        '<ul class="cd-search__res cd-search__intent-list">' +
          '<li>' +
            '<a class="cd-search__intent-link" href="' + intent.url + '">' +
              '<span class="cd-search__intent-icon" aria-hidden="true">↗</span>' +
              '<span class="cd-search__intent-copy">' +
                '<strong>' + escapeHtml(intent.label) + '</strong>' +
                '<small>Shop matching gifts</small>' +
              '</span>' +
              '<span class="cd-search__intent-arrow" aria-hidden="true">↗</span>' +
            '</a>' +
          '</li>' +
        '</ul>' +
      '</div>';
  }

  function showEmptyState() {
    if (intents) intents.innerHTML = '';
    results.innerHTML = '';
    initial.style.display = '';
  }

  function run(query) {
    if (!query || query.length < 2) {
      showEmptyState();
      return;
    }

    initial.style.display = 'none';

    var intent = findIntent(query);
    renderIntent(intent);

    var predictiveQuery = intent && intent.search ? intent.search : query;

    if (controller && controller.abort) {
      controller.abort();
    }

    controller = window.AbortController ? new AbortController() : null;

    var requestUrl =
      routes.predictive_search +
      '?q=' + encodeURIComponent(predictiveQuery) +
      '&resources[type]=product,article,collection' +
      '&resources[limit]=6' +
      '&section_id=cadoo-predictive';

    var requestOptions = controller
      ? { signal: controller.signal }
      : {};

    fetch(requestUrl, requestOptions)
      .then(function (response) {
        if (!response.ok) throw new Error('Search request failed');
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var inner = doc.querySelector('[data-cd-predictive-inner]');

        results.innerHTML = inner
          ? inner.innerHTML
          : '<p class="cd-note">Nothing matched that yet.</p>';
      })
      .catch(function (error) {
        if (error.name === 'AbortError') return;

        results.innerHTML =
          '<p class="cd-note">Search is unavailable right now. Please try again.</p>';
      });
  }

  input.addEventListener('input', function () {
    clearTimeout(timer);

    var value = input.value.trim();

    timer = setTimeout(function () {
      run(value);
    }, 220);
  });

  input.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      input.value = '';
      showEmptyState();
    }
  });
}


  /* ---------------- homepage scroll reveal ---------------------------------
     Small reusable, homepage-scoped IntersectionObserver utility. Reveals
     `selector` elements once each starts intersecting (per `options`, an
     IntersectionObserver init dict - threshold/rootMargin/etc.) by adding
     `revealedClass` (after first adding `observeClass`, which is what puts
     the element in its pre-reveal CSS state - see the section's own
     {% style %} block for the actual opacity/transform rules), then stops
     observing that element. No-ops completely - leaving every match at its
     normal, fully-visible CSS state - when: this isn't the homepage
     (checked via the body.template-index class set in layout/theme.liquid),
     IntersectionObserver isn't supported, the visitor prefers reduced
     motion, or there's nothing to observe. That makes "do nothing" the safe
     fallback for JS failures as well as for unsupported browsers. */
  function cdHomepageReveal(selector, observeClass, revealedClass, options) {
    if (!document.body.classList.contains('template-index')) return;
    if (typeof window.IntersectionObserver !== 'function') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var els = $$(selector);
    if (!els.length) return;
    var io = new window.IntersectionObserver(function (entries, observer) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add(revealedClass);
        observer.unobserve(entry.target);
      });
    }, options || { threshold: 0 });
    els.forEach(function (el) {
      el.classList.add(observeClass);
      io.observe(el);
    });
  }

  function initFromAnywhereReveal() {
    /* .cd-fa is the section's own content (copy + illustration) - the
       decorative paper-intersection sections above/below it in
       templates/index.json are separate sections, never nested inside
       this one. The trigger point is anchored to where that content sits
       in the viewport rather than to how much of the section's own (tall,
       padding-heavy) box is visible: shrinking the observer's effective
       viewport by 28% off the bottom edge, with threshold: 0, means
       "intersecting" only flips true once the section's top has scrolled
       up to roughly 72% of the viewport height (within the requested
       70-75% range) - instead of firing as soon as a sliver of the
       section appears at the very bottom of the screen. */
    cdHomepageReveal('.cd-fa', 'is-observing', 'is-revealed', { threshold: 0, rootMargin: '0px 0px -28% 0px' });
  }

  /* The cart and the overlay contract are owned here and only here.
     Publish this API before startup so a product initialization failure cannot
     make the modal controller fall back merely because window.Cadoo was never
     assigned. */
  window.Cadoo = {
    open: openPanel,
    close: closePanel,
    closeAll: closeAll,
    renderCart: renderCart,
    refreshCart: refreshCart,
    getCart: getCart,
    getCartState: function () { return cartState; },
    cartSubtotal: cartSubtotal,
    freeShipState: freeShipState,
    addToCart: addToCart,
    money: money,
    toast: toast,
    initAccordions: initAccordions,
    syncWish: syncWish,
    initPdp: initPdp,
    claimed: offerClaimed,
    claim: offerClaim
  };

  /* ---------------- init ---------------- */
  function init() {
    initAnnounce();
    initMega();
    initAccordions();
    initSearch();
    initPdp();
    initFromAnywhereReveal();
    syncWish();
    offerApplyState();
    getCart();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
